/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/

import JSG from '@cedalo/jsg-ui';
import { Box, FormControl, FormGroup, Grid, IconButton, TextField, Tooltip } from '@mui/material';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import SvgIcon from '@mui/material/SvgIcon';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { graphManager } from '../../../GraphManager';
import { intl } from '../../../helper/IntlGlobalProvider';
import SettingsPaper from '../../utils/SettingsPaper';
import { StreamSheetAccordion } from '../../utils/StreamSheetAccordion';
import CellRangeComponent from '../CellRangeComponent';
import { useAsyncReference, useJSGNotification } from '../../utils/JSGHooks';
import ColorComponent from '../ColorComponent';
import { StreamChartSettings } from './StreamChartSettings';

const MyInputComponent = React.forwardRef((props, ref) => {
	const { component: Component, ...other } = props;
	React.useImperativeHandle(ref, () => ({
		focus: () => {}
	}));
	return <Component {...other} />;
});

export function ShapeSettings(props) {
	const [expanded, setExpanded] = useState('panelGeometrie');
	const [errors, setErrors] = useState([]);
	const [item, setItem] = useAsyncReference();
	const [view, setView] = useAsyncReference();
	const [sheetView, setSheetView] = useAsyncReference();
	const [shapeSheet, setShapeSheet] = useAsyncReference();
	const [, setRandom] = useState(-1);
	const [tabSelected, setTabSelected] = useAsyncReference();
	const [layoutMargin, setLayoutMargin] = useState(0);
	const [layoutGap, setLayoutGap] = useState(0);
	const [layoutNewLine, setLayoutNewLine] = useState(0);

	const handleChange = (exp) => {
		setExpanded(exp === expanded ? undefined : exp);
	};

	const getEventFormula = (expr) => {
		const val = expr.getValue();
		if (val === '') {
			return '';
		}

		const tmpExpr = new JSG.Expression(0, expr.getValue());
		tmpExpr.evaluateForSheet(shapeSheet.current.model);
		return tmpExpr.toLocaleString(JSG.getParserLocaleSettings(), {
			sheet: sheetView.current.getItem().model,
			useName: true
		});
	};

	const getFormula = (expr, round) => {
		return expr.toLocaleString(JSG.getParserLocaleSettings(), {
			sheet: shapeSheet.current.model,
			useName: true,
			round
		});
	};

	const getError = (label) => {
		const res = errors.filter((error) => {
			return error.label === label;
		});

		return res.length ? res[0] : undefined;
	};

	const isFormula = (value) => {
		return value.length && value.charAt(0) === '=';
	};

	const validateCoordinate = (value, label) => {
		if (!isFormula(value) && !JSG.Numbers.isNumber(Number(value))) {
			if (!getError(label)) {
				errors.push({
					value,
					label,
					message: intl.formatMessage({ id: 'Alert.InvalidNumber' }, {})
				});
				setErrors(errors);
				setRandom(Math.random());
			}
			return false;
		}

		const error = getError(label);
		if (error) {
			JSG.Arrays.remove(errors, error);
			setErrors(errors);
			setRandom(Math.random());
		}

		return true;
	};

	const validateName = (name, label) => {
		let used = false;

		if (!isFormula(name) && name !== '') {
			JSG.GraphUtils.traverseItem(
				graphManager.getGraph(),
				(litem) => {
					if (item.current.getId() !== litem.getId() && name === litem.getName().getValue()) {
						used = true;
					}
				},
				false
			);

			if (used || name === '') {
				if (!getError(label)) {
					errors.push({
						value: name,
						label,
						message: intl.formatMessage({ id: 'Alert.SheetDoubleName' }, {})
					});
					setErrors(errors);
					setRandom(Math.random());
				}
				return false;
			}
			if (JSG.Numbers.canBeNumber(name)) {
				if (!getError(label)) {
					errors.push({
						value: name,
						label,
						message: intl.formatMessage({ id: 'Alert.NoNumber' }, {})
					});
					setErrors(errors);
					setRandom(Math.random());
				}
				return false;
			}
		}

		const error = getError(label);
		if (error) {
			JSG.Arrays.remove(errors, error);
			setErrors(errors);
			setRandom(Math.random());
		}

		return true;
	};

	const getPropertyHandler = (label, handler, expression, validate, round = 0) => {
		const error = getError(label);

		return (
			<TextField
				key={label}
				variant="outlined"
				size="small"
				fullWidth
				error={error !== undefined}
				helperText={error === undefined ? undefined : error.message}
				margin="normal"
				label={intl.formatMessage({ id: label })}
				onBlur={(event) => handler(event, error)}
				InputLabelProps={{ shrink: true }}
				InputProps={{
					inputComponent: MyInputComponent,
					inputProps: {
						label,
						validate,
						onlyReference: false,
						component: CellRangeComponent,
						sheetView: sheetView.current,
						range: error ? error.value : getFormula(expression, round)
					}
				}}
			/>
		);
	};

	const getExpression = (event, fromValue = false) => {
		try {
			return shapeSheet.current
				.textToExpression(String(fromValue ? event.target.value : event.target.textContent));
		} catch (e) {
			sheetView.current.notifyMessage({
				message: e.message,
				focusIndex: e.index !== undefined ? e.index + 1 : 1
			});
			return undefined;
		}
	};

	const getEventExpression = (event) => {
		let formula = String(event.target.textContent);
		const isFormulaStr = formula.charAt(0) === '=';
		if (isFormulaStr) {
			const expr = getExpression(event);
			if (!expr || !expr.expression) {
				return undefined;
			}
			formula = expr.expression.getFormula();
		}
		return new JSG.StringExpression(formula);
	};

	const getX = () => {
		const type = item.current.getShape().getType();
		let ret;

		if (type === JSG.LineShape.TYPE) {
			const coor = item.current.getStartCoordinate();
			ret = coor.getX().hasFormula() ? coor.getX() : new JSG.NumberExpression(item.current.getStartPoint().x);
		} else {
			ret = item.current.getPin().getX();
		}

		return ret;
	};

	const getY = () => {
		const type = item.current.getShape().getType();
		let ret;

		if (type === JSG.LineShape.TYPE) {
			const coor = item.current.getStartCoordinate();
			ret = coor.getY().hasFormula() ? coor.getY() : new JSG.NumberExpression(item.current.getStartPoint().y);
		} else {
			ret = item.current.getPin().getY();
		}

		return ret;
	};

	const getWidth = () => {
		const type = item.current.getShape().getType();
		let ret;

		if (type === JSG.LineShape.TYPE) {
			const coor = item.current.getEndCoordinate();
			ret = coor.getX().hasFormula() ? coor.getX() : new JSG.NumberExpression(item.current.getEndPoint().x);
		} else {
			ret = item.current.getWidth();
		}

		return ret;
	};

	const getHeight = () => {
		const type = item.current.getShape().getType();
		let ret;

		if (type === JSG.LineShape.TYPE) {
			const coor = item.current.getEndCoordinate();
			ret = coor.getY().hasFormula() ? coor.getY() : new JSG.NumberExpression(item.current.getEndPoint().y);
		} else {
			ret = item.current.getHeight();
		}

		return ret;
	};

	const getRotationCenter = () => {
		const pin = item.current.getPin();
		let x = pin.getLocalX().getFormula();
		let y = pin.getLocalY().getFormula();
		let ret;

		if (x) {
			x = x.replace(/\s+/g, '');
		}
		if (y) {
			y = y.replace(/\s+/g, '');
		}

		if (x === undefined) {
			if (y === undefined) {
				ret = 0;
			} else if (y.indexOf('HEIGHT*0.5') !== -1) {
				ret = 3;
			} else {
				ret = 6;
			}
		} else if (x.indexOf('WIDTH*0.5') !== -1) {
			if (y === undefined) {
				ret = 1;
			} else if (y.indexOf('HEIGHT*0.5') !== -1) {
				ret = 4;
			} else {
				ret = 7;
			}
		} else if (y === undefined) {
			ret = 2;
		} else if (y.indexOf('HEIGHT*0.5') !== -1) {
			ret = 5;
		} else {
			ret = 8;
		}

		return ret;
	};

	const handleX = (event) => {
		const expr = getExpression(event).expression;
		if (!expr) {
			return;
		}
		const line = item.current.getShape().getType() === JSG.LineShape.TYPE;
		let cmd;

		if (line) {
			const startCoor = item.current.getStartCoordinate();

			if (expr.hasFormula()) {
				startCoor.setX(expr);
			} else {
				const point = new JSG.Point(expr.getValue(), 0);
				item.current.translateFromParent(point);
				startCoor.setX(new JSG.NumberExpression(point.x));
			}

			cmd = new JSG.SetLineCoordinateAtCommand(item.current, 0, startCoor);
		} else {
			item.current.getPin().setX(expr);
			cmd = new JSG.SetPinCommand(item.current, item.current.getPin());
		}

		graphManager.synchronizedExecute(cmd);
	};

	const handleY = (event) => {
		const expr = getExpression(event).expression;
		if (!expr) {
			return;
		}
		const line = item.current.getShape().getType() === JSG.LineShape.TYPE;
		let cmd;

		if (line) {
			const startCoor = item.current.getStartCoordinate();

			if (expr.hasFormula()) {
				startCoor.setY(expr);
			} else {
				const point = new JSG.Point(0, expr.getValue());
				item.current.translateFromParent(point);
				startCoor.setY(new JSG.NumberExpression(point.y));
			}

			cmd = new JSG.SetLineCoordinateAtCommand(item.current, 0, startCoor);
		} else {
			item.current.getPin().setY(expr);
			cmd = new JSG.SetPinCommand(item.current, item.current.getPin());
		}

		graphManager.synchronizedExecute(cmd);
	};

	const handleWidth = (event) => {
		const expr = getExpression(event).expression;
		if (!expr) {
			return;
		}
		const line = item.current.getShape().getType() === JSG.LineShape.TYPE;
		let cmd;

		if (line) {
			const endCoor = item.current.getEndCoordinate();

			if (expr.hasFormula()) {
				endCoor.setX(expr);
			} else {
				const point = new JSG.Point(expr.getValue(), 0);
				item.current.translateFromParent(point);
				endCoor.setX(new JSG.NumberExpression(point.x));
			}

			cmd = new JSG.SetLineCoordinateAtCommand(item.current, 1, endCoor);
		} else {
			cmd = new JSG.SetSizeCommand(item.current, new JSG.Size(expr, item.current.getHeight()));
		}
		graphManager.synchronizedExecute(cmd);
	};

	const handleHeight = (event) => {
		const expr = getExpression(event).expression;
		if (!expr) {
			return;
		}
		const line = item.current.getShape().getType() === JSG.LineShape.TYPE;
		let cmd;

		if (line) {
			const endCoor = item.current.getEndCoordinate();

			if (expr.hasFormula()) {
				endCoor.setY(expr);
			} else {
				const point = new JSG.Point(0, expr.getValue());
				item.current.translateFromParent(point);
				endCoor.setY(new JSG.NumberExpression(point.y));
			}

			cmd = new JSG.SetLineCoordinateAtCommand(item.current, 1, endCoor);
		} else {
			cmd = new JSG.SetSizeCommand(item.current, new JSG.Size(item.current.getWidth(), expr));
		}
		graphManager.synchronizedExecute(cmd);
	};

	const handleRotation = (event) => {
		const expr = getExpression(event);
		if (expr) {
			const cmd = new JSG.RotateItemCommand(item.current, expr.expression);
			graphManager.synchronizedExecute(cmd);
		}
	};

	const handleRotationCenter = (event) => {
		const pin = item.current.getPin();
		const local = pin.getLocalPoint();

		switch (event.target.value) {
			case '0':
				pin.setLocalCoordinate(new JSG.NumberExpression(0), new JSG.NumberExpression(0));
				break;
			case '1':
				pin.setLocalCoordinate(new JSG.NumberExpression(local.x, 'WIDTH * 0.5'), new JSG.NumberExpression(0));
				break;
			case '2':
				pin.setLocalCoordinate(new JSG.NumberExpression(local.x, 'WIDTH'), new JSG.NumberExpression(0));
				break;
			case '3':
				pin.setLocalCoordinate(new JSG.NumberExpression(0), new JSG.NumberExpression(local.y, 'HEIGHT * 0.5'));
				break;
			default:
			case '4':
				pin.setLocalCoordinate(
					new JSG.NumberExpression(local.x, 'WIDTH * 0.5'),
					new JSG.NumberExpression(local.y, 'HEIGHT * 0.5')
				);
				break;
			case '5':
				pin.setLocalCoordinate(
					new JSG.NumberExpression(local.x, 'WIDTH'),
					new JSG.NumberExpression(local.y, 'HEIGHT * 0.5')
				);
				break;
			case '6':
				pin.setLocalCoordinate(new JSG.NumberExpression(0), new JSG.NumberExpression(local.y, 'HEIGHT'));
				break;
			case '7':
				pin.setLocalCoordinate(
					new JSG.NumberExpression(local.x, 'WIDTH * 0.5'),
					new JSG.NumberExpression(local.y, 'HEIGHT')
				);
				break;
			case '8':
				pin.setLocalCoordinate(
					new JSG.NumberExpression(local.x, 'WIDTH'),
					new JSG.NumberExpression(local.y, 'HEIGHT')
				);
				break;
		}

		const cmd = new JSG.SetPinCommand(item.current, pin);

		graphManager.synchronizedExecute(cmd);
		// trigger render
		setRandom(Math.random());
	};

	const handlePointRange = (event) => {
		const expr = getExpression(event);
		if (!expr) {
			return;
		}
		const cmd = new JSG.SetPointSourceCommand(item.current, expr.expression);
		graphManager.synchronizedExecute(cmd);
	};

	const handleName = (event) => {
		if (getError('ShapeSettings.Name')) {
			return;
		}
		const expr = getExpression(event);
		if (expr) {
			const cmd = new JSG.SetNameCommand(item.current, expr.expression);
			graphManager.synchronizedExecute(cmd);
		}
	};

	const handleText = (event) => {
		const expr = getExpression(event);
		if (!expr) {
			return;
		}
		const cmd = new JSG.SetTextCommand(item.current, item.current.getText(), expr.expression);
		graphManager.synchronizedExecute(cmd);
	};

	const handleAttribute = (event, name, fromValue = false) => {
		const expr = getExpression(event, fromValue);
		const exp = item.current.getAttributeAtPath(name).getExpression();
		if (expr) {
			exp.useFormula = expr.expression.hasFormula();
			graphManager.synchronizedExecute(new JSG.SetAttributeAtPathCommand(item.current, name, expr.expression));
		}
	};

	const handleEventAttribute = (event, name) => {
		const expr = getEventExpression(event);
		if (expr) {
			graphManager.synchronizedExecute(new JSG.SetAttributeAtPathCommand(item.current, name, expr));
		}
	};

	const getLayoutMode = (name) => {
		return item.current.getAttributeAtPath(name).getValue();
	};

	const handleLayoutMode = (event, name, expr) => {
		const cmd = new JSG.SetAttributeAtPathCommand(item.current, name, expr);

		graphManager.synchronizedExecute(cmd);
		setRandom(Math.random);
	};

	const getLayoutType = () => {
		return item.current.getLayoutCellAttributes().getLayout().getValue();
	};

	const handleLayoutType = (event) => {
		const path = JSG.AttributeUtils.createPath(JSG.LayoutCellAttributes.NAME, JSG.LayoutCellAttributes.LAYOUT);
		const cmp = new JSG.CompoundCommand();
		cmp.add(new JSG.SetAttributeAtPathCommand(item.current, path, event.target.value));
		item.current.handleLayoutTypeChange(event.target.value, cmp);

		graphManager.synchronizedExecute(cmp);
		setRandom(Math.random);
	};

	const handleLayoutColumns = (event) => {
		const path = JSG.AttributeUtils.createPath(JSG.LayoutCellAttributes.NAME, JSG.LayoutCellAttributes.SECTIONS);
		const cmp = new JSG.CompoundCommand();
		cmp.add(new JSG.SetAttributeAtPathCommand(item.current, path, Number(event.target.value)));
		item.current.handleLayoutColumnChange(item.current.getItemCount(), Number(event.target.value), cmp);

		graphManager.synchronizedExecute(cmp);
		setRandom(Math.random);
	};

	const handleLayoutMargin = (event) => {
		setLayoutMargin(event.target.value);
	};

	const handleLayoutMarginBlur = (event) => {
		const margin = Number(event.target.value);

		if (Number.isNaN(margin) || margin < 0 || margin > 5000) {
			return;
		}

		const path = JSG.AttributeUtils.createPath(JSG.LayoutCellAttributes.NAME, JSG.LayoutCellAttributes.MARGIN);
		const cmd = new JSG.SetAttributeAtPathCommand(item.current, path, event.target.value);

		graphManager.synchronizedExecute(cmd);
		setRandom(Math.random);
	};

	const handleLayoutGap = (event) => {
		setLayoutGap(event.target.value);
	};

	const handleLayoutGapBlur = (event) => {
		const gap = Number(event.target.value);

		if (Number.isNaN(gap) || gap < 0 || gap > 5000) {
			return;
		}

		const path = JSG.AttributeUtils.createPath(JSG.LayoutCellAttributes.NAME, JSG.LayoutCellAttributes.GAP);
		const cmd = new JSG.SetAttributeAtPathCommand(item.current, path, event.target.value);

		graphManager.synchronizedExecute(cmd);
		setRandom(Math.random);
	};

	const handleLayoutNewLine = (event) => {
		setLayoutNewLine(event.target.value);
	};

	const handleLayoutNewLineBlur = (event) => {
		const value = Number(event.target.value);

		if (Number.isNaN(value) || value < 0) {
			return;
		}

		const path = JSG.AttributeUtils.createPath(JSG.LayoutCellAttributes.NAME, JSG.LayoutCellAttributes.NEWLINE);
		const cmd = new JSG.SetAttributeAtPathCommand(item.current, path, event.target.value);

		graphManager.synchronizedExecute(cmd);
		setRandom(Math.random);
	};

	const getLabel = (option) => {
		if (option.label === undefined) {
			return option.value;
		}

		return `${intl.formatMessage({ id: option.label }, {})}`;
	};

	const getAttributeHandler = (label, name, type, round = 0, options) => {
		const expr = item.current.getAttributeAtPath(name).getExpression();
		if (!expr.useFormula && !expr.hasFormula()) {
			switch (type) {
				case 'bool':
					return (
						<Box
							sx={{
								display: 'flex',
								justifyContent: 'space-between',
								width: '100%'
							}}
							key={name}
						>
							<FormControlLabel
								control={
									<Checkbox
										color="primary"
										checked={expr.getValue()}
										onChange={(ev, checked) => {
											graphManager.synchronizedExecute(
												new JSG.SetAttributeAtPathCommand(
													item.current,
													name,
													new JSG.BooleanExpression(checked)
												)
											);
										}}
									/>
								}
								label={intl.formatMessage({ id: label })}
							/>
							<Tooltip
								enterDelay={300}
								title={<FormattedMessage id="Tooltip.SetFormula" defaultMessage="Use Formula" />}
							>
								<IconButton
									onClick={() => {
										expr.useFormula = true;
										setRandom(Math.random);
									}}
									size="small"
								>
									<SvgIcon style={{ width: '15px', height: '15px' }}>
										<path d="M12.42,5.29C11.32,5.19 10.35,6 10.25,7.11L10,10H12.82V12H9.82L9.38,17.07C9.18,19.27 7.24,20.9 5.04,20.7C3.79,20.59 2.66,19.9 2,18.83L3.5,17.33C3.83,18.38 4.96,18.97 6,18.63C6.78,18.39 7.33,17.7 7.4,16.89L7.82,12H4.82V10H8L8.27,6.93C8.46,4.73 10.39,3.1 12.6,3.28C13.86,3.39 15,4.09 15.66,5.17L14.16,6.67C13.91,5.9 13.23,5.36 12.42,5.29M22,13.65L20.59,12.24L17.76,15.07L14.93,12.24L13.5,13.65L16.35,16.5L13.5,19.31L14.93,20.72L17.76,17.89L20.59,20.72L22,19.31L19.17,16.5L22,13.65Z" />
									</SvgIcon>
								</IconButton>
							</Tooltip>
						</Box>
					);
				case 'color':
					return (
						<Box
							sx={{
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
								width: '100%'
							}}
							key={name}
						>
							<FormControl
								size="small"
								variant="outlined"
								margin="normal"
								style={{
									display: 'inline-flex',
									width: '100%'
								}}
							>
								<ColorComponent
									labelFontSize="1rem"
									label={intl.formatMessage({ id: label })}
									transparent
									color={expr.getValue()}
									onChange={(color) => {
										graphManager.synchronizedExecute(
											new JSG.SetAttributeAtPathCommand(
												item.current,
												name,
												new JSG.StringExpression(color)
											)
										);
									}}
								/>
							</FormControl>
							<Tooltip
								enterDelay={300}
								title={<FormattedMessage id="Tooltip.SetFormula" defaultMessage="Use Formula" />}
							>
								<IconButton
									sx={{
										height: '30px',
										marginTop: '5px'
									}}
									onClick={() => {
										expr.useFormula = true;
										setRandom(Math.random);
									}}
									size="small"
								>
									<SvgIcon style={{ width: '15px', height: '15px' }}>
										<path d="M12.42,5.29C11.32,5.19 10.35,6 10.25,7.11L10,10H12.82V12H9.82L9.38,17.07C9.18,19.27 7.24,20.9 5.04,20.7C3.79,20.59 2.66,19.9 2,18.83L3.5,17.33C3.83,18.38 4.96,18.97 6,18.63C6.78,18.39 7.33,17.7 7.4,16.89L7.82,12H4.82V10H8L8.27,6.93C8.46,4.73 10.39,3.1 12.6,3.28C13.86,3.39 15,4.09 15.66,5.17L14.16,6.67C13.91,5.9 13.23,5.36 12.42,5.29M22,13.65L20.59,12.24L17.76,15.07L14.93,12.24L13.5,13.65L16.35,16.5L13.5,19.31L14.93,20.72L17.76,17.89L20.59,20.72L22,19.31L19.17,16.5L22,13.65Z" />
									</SvgIcon>
								</IconButton>
							</Tooltip>
						</Box>
					);
				case 'enum':
					if (!options) {
						break;
					}
					return (
						<Box
							sx={{
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
								width: '100%'
							}}
							key={name}
						>
							<TextField
								variant="outlined"
								size="small"
								label={intl.formatMessage({ id: label })}
								select
								fullWidth
								margin="normal"
								value={expr.getValue()}
								onChange={(event) => {
									handleAttribute(event, name, true);
								}}
							>
								{options.map((option) => (
									<MenuItem dense key={option.value} value={option.value}>
										{getLabel(option)}
									</MenuItem>
								))}
							</TextField>
							<Tooltip
								enterDelay={300}
								title={<FormattedMessage id="Tooltip.SetFormula" defaultMessage="Use Formula" />}
							>
								<IconButton
									sx={{
										height: '30px',
										marginTop: '5px'
									}}
									onClick={() => {
										expr.useFormula = true;
										setRandom(Math.random);
									}}
									size="small"
								>
									<SvgIcon style={{ width: '15px', height: '15px' }}>
										<path d="M12.42,5.29C11.32,5.19 10.35,6 10.25,7.11L10,10H12.82V12H9.82L9.38,17.07C9.18,19.27 7.24,20.9 5.04,20.7C3.79,20.59 2.66,19.9 2,18.83L3.5,17.33C3.83,18.38 4.96,18.97 6,18.63C6.78,18.39 7.33,17.7 7.4,16.89L7.82,12H4.82V10H8L8.27,6.93C8.46,4.73 10.39,3.1 12.6,3.28C13.86,3.39 15,4.09 15.66,5.17L14.16,6.67C13.91,5.9 13.23,5.36 12.42,5.29M22,13.65L20.59,12.24L17.76,15.07L14.93,12.24L13.5,13.65L16.35,16.5L13.5,19.31L14.93,20.72L17.76,17.89L20.59,20.72L22,19.31L19.17,16.5L22,13.65Z" />
									</SvgIcon>
								</IconButton>
							</Tooltip>
						</Box>
					);

				default:
					break;
			}
		}

		return (
			<TextField
				key={label}
				variant="outlined"
				fullWidth
				size="small"
				margin="normal"
				label={intl.formatMessage({ id: label })}
				onBlur={(event) => handleAttribute(event, name)}
				InputLabelProps={{ shrink: true }}
				InputProps={{
					inputComponent: MyInputComponent,
					inputProps: {
						onlyReference: false,
						component: CellRangeComponent,
						inputEditorType: options instanceof Array ? 'string' : options,
						inputEditorOptions: undefined, // options instanceof Array ? options : undefined,
						sheetView: sheetView.current,
						range: getFormula(expr, round)
					}
				}}
			/>
		);
	};

	const getEventAttributeHandler = (label, name) => {
		return (
			<TextField
				variant="outlined"
				fullWidth
				size="small"
				margin="normal"
				label={intl.formatMessage({ id: label })}
				onBlur={(event) => handleEventAttribute(event, name)}
				InputLabelProps={{ shrink: true }}
				InputProps={{
					inputComponent: MyInputComponent,
					inputProps: {
						onlyReference: false,
						component: CellRangeComponent,
						sheetView: sheetView.current,
						range: getEventFormula(item.current.getAttributeAtPath(name).getExpression())
					}
				}}
			/>
		);
	};

	const getFormatPath = (name) => {
		return JSG.AttributeUtils.createPath(JSG.FormatAttributes.NAME, name);
	};

	const getTextFormatPath = (name) => {
		return JSG.AttributeUtils.createPath(JSG.TextFormatAttributes.NAME, name);
	};

	const getItemPath = (name) => {
		return JSG.AttributeUtils.createPath(JSG.ItemAttributes.NAME, name);
	};

	const getEventPath = (name) => {
		return JSG.AttributeUtils.createPath(JSG.EventAttributes.NAME, name);
	};

	const getSettingsTitle = () => {
		if (!item.current) {
			return <FormattedMessage id="ShapeSettings.Title" defaultMessage="Shape Settings" />;
		}

		switch (item.current.getItemType()) {
			case 'text':
				return <FormattedMessage id="ShapeSettings.SettingsText" defaultMessage="Text" />;
			case 'streamchart':
				return <FormattedMessage id="ShapeSettings.SettingsChart" defaultMessage="Chart" />;
			case 'sheetknobnode':
				return <FormattedMessage id="ShapeSettings.SettingsKnob" defaultMessage="Knob" />;
			case 'sheetslidernode':
				return <FormattedMessage id="ShapeSettings.SettingsSlider" defaultMessage="Slider" />;
			case 'sheetbuttonnode':
				return <FormattedMessage id="ShapeSettings.SettingsButton" defaultMessage="Button" />;
			case 'sheetcheckboxnode':
				return <FormattedMessage id="ShapeSettings.SettingsCheckbox" defaultMessage="Checkbox" />;
			case 'sheetjsonnode':
				return <FormattedMessage id="ShapeSettings.SettingsJsonNode" defaultMessage="JSON Box" />;
			case 'layoutnode':
				return <FormattedMessage id="ShapeSettings.SettingsDashboard" defaultMessage="Dashboard" />;
			case 'layoutcell':
				return <FormattedMessage id="ShapeSettings.SettingsLayout" defaultMessage="Layout" />;
			default:
				return <FormattedMessage id="ShapeSettings.Title" defaultMessage="Shape Settings" />;
		}
	};

	const getComponentTitle = () => {
		if (!item.current) {
			return undefined;
		}
		switch (item.current.getItemType()) {
			case 'text':
				return <FormattedMessage id="ShapeSettings.Text" defaultMessage="Text" />;
			case 'streamchart':
				return <FormattedMessage id="ShapeSettings.Chart" defaultMessage="Chart" />;
			case 'sheetknobnode':
				return <FormattedMessage id="ShapeSettings.Knob" defaultMessage="Knob" />;
			case 'sheetslidernode':
				return <FormattedMessage id="ShapeSettings.Slider" defaultMessage="Slider" />;
			case 'sheetbuttonnode':
				return <FormattedMessage id="ShapeSettings.Button" defaultMessage="Button" />;
			case 'sheetcheckboxnode':
				return <FormattedMessage id="ShapeSettings.Checkbox" defaultMessage="Checkbox" />;
			case 'sheetjsonnode':
				return <FormattedMessage id="ShapeSettings.Json" defaultMessage="JSON Box" />;
			case 'layoutnode':
				return <FormattedMessage id="ShapeSettings.Dashboard" defaultMessage="Dashboard" />;
			case 'layoutcell':
				return <FormattedMessage id="ShapeSettings.Layout" defaultMessage="Layout" />;
			default:
				return undefined;
		}
	};

	const getComponent = () => {
		if (!item.current) {
			return undefined;
		}
		switch (item.current.getItemType()) {
			case 'streamchart':
				return (
					<StreamChartSettings chart={item.current} chartView={view.current} shapeSheet={shapeSheet.current} sheetView={sheetView.current} />
				);
			case 'sheetknobnode':
				return (
					<div>
						{[
							getAttributeHandler('ShapeSettings.Label', 'title', 'string'),
							getAttributeHandler('ShapeSettings.Value', 'value', 'number'),
							getAttributeHandler('ShapeSettings.Minimum', 'min', 'number', -1),
							getAttributeHandler('ShapeSettings.Maximum', 'max', 'number', -1),
							getAttributeHandler('ShapeSettings.Step', 'step', 'number', -1),
							getAttributeHandler('ShapeSettings.Marker', 'marker', 'string'),
							getAttributeHandler('ShapeSettings.FormatRange', 'formatrange', 'range'),
							getAttributeHandler('ShapeSettings.StartAngle', 'start', 'number', 2),
							getAttributeHandler('ShapeSettings.EndAngle', 'end', 'number', 2)
						]}
					</div>
				);
			case 'sheetslidernode':
				return (
					<div>
						{[
							getAttributeHandler('ShapeSettings.Label', 'title', 'string'),
							getAttributeHandler('ShapeSettings.Value', 'value', 'number'),
							getAttributeHandler('ShapeSettings.Minimum', 'min', 'number', -1),
							getAttributeHandler('ShapeSettings.Maximum', 'max', 'number', -1),
							getAttributeHandler('ShapeSettings.Step', 'step', 'number', -1),
							getAttributeHandler('ShapeSettings.Marker', 'marker', 'string'),
							getAttributeHandler('ShapeSettings.FormatRange', 'formatrange', 'range')
						]}
					</div>
				);
			case 'sheetbuttonnode':
				return (
					<div>
						{[
							getAttributeHandler('ShapeSettings.Label', 'title', 'string'),
							getAttributeHandler('ShapeSettings.Value', 'value', 'number')
						]}
					</div>
				);
			case 'sheetcheckboxnode':
				return (
					<div>
						{[
							getAttributeHandler('ShapeSettings.Label', 'title', 'string'),
							getAttributeHandler('ShapeSettings.Value', 'value', 'number')
						]}
					</div>
				);
			case 'text': {
				const exprType = item.current.getAttributeAtPath('GRAPHITEM:type').getExpression();
				const type = exprType ? exprType.getValue() : '';

				return (
					<div>
						{getPropertyHandler('ShapeSettings.Text', handleText, item.current.getText())}
						{getAttributeHandler('ShapeSettings.Type', 'GRAPHITEM:type', 'enum', 0, [
							{value: '0', label: 'ShapeSettings.View'},
							{value: '1', label: 'ShapeSettings.Edit'},
							{value: '2', label: 'ShapeSettings.Select'},
							{value: '3', label: 'ShapeSettings.SelectEdit'}
						])
						}
						{type === 1 ? getAttributeHandler('ShapeSettings.ReturnAction', 'GRAPHITEM:returnaction', 'enum', 0, [
							{value: '0', label: 'ShapeSettings.NewLine'},
							{value: '1', label: 'ShapeSettings.AcceptValue'}
						]) : null
						}
						{type === 2 || type === 3 ? getAttributeHandler('ShapeSettings.OptionsRange', 'GRAPHITEM:optionsrange', 'range') : null}
					</div>
				);
			}
			case 'layoutcell':
				return (
					<div>
						{[
							<TextField
								key="layoutcell"
								variant="outlined"
								size="small"
								margin="normal"
								select
								fullWidth
								value={getLayoutType()}
								onChange={(event) => handleLayoutType(event)}
								label={<FormattedMessage id="ShapeSettings.Layout" defaultMessage="Layout" />}
							>
								<MenuItem value="none">
									<FormattedMessage id="ShapeSettings.None" defaultMessage="None" />
								</MenuItem>
								<MenuItem value="row">
									<FormattedMessage id="ShapeSettings.RowLayout" defaultMessage="Row Layout" />
								</MenuItem>
								<MenuItem value="column">
									<FormattedMessage id="ShapeSettings.ColumnLayout" defaultMessage="Column Layout" />
								</MenuItem>
							</TextField>,
							item.current.getLayoutCellAttributes().getLayout().getValue() === 'column'
								? [
										<TextField
											key="layoutcolumns"
											variant="outlined"
											size="small"
											fullWidth
											margin="normal"
											select
											value={item.current.getLayoutCellAttributes().getSections().getValue()}
											onChange={(event) => handleLayoutColumns(event)}
											label={
												<FormattedMessage
													id="ShapeSettings.LayoutColumns"
													defaultMessage="Layout Columns"
												/>
											}
										>
											<MenuItem value="2">2</MenuItem>
											<MenuItem value="3">3</MenuItem>
											<MenuItem value="4">4</MenuItem>
											<MenuItem value="5">5</MenuItem>
											<MenuItem value="6">6</MenuItem>
											<MenuItem value="7">7</MenuItem>
											<MenuItem value="8">8</MenuItem>
											<MenuItem value="9">9</MenuItem>
											<MenuItem value="10">10</MenuItem>
										</TextField>
								  ]
								: null,
							item.current.getLayoutCellAttributes().getLayout().getValue() === 'row'
								? [
										<TextField
											key="layoutmargin"
											variant="outlined"
											size="small"
											margin="normal"
											fullWidth
											value={layoutMargin}
											onChange={(event) => handleLayoutMargin(event)}
											onBlur={(event) => handleLayoutMarginBlur(event)}
											onKeyPress={(event) => {
												if (event.key === 'Enter') {
													if (document.activeElement) {
														document.activeElement.blur();
													}
													handleLayoutMarginBlur(event);
													event.preventDefault();
												}
											}}
											label={
												<FormattedMessage
													id="ShapeSettings.LayoutMargin"
													defaultMessage="Layout Margin"
												/>
											}
										/>,
										<TextField
											key="layoutgap"
											variant="outlined"
											size="small"
											fullWidth
											margin="normal"
											value={layoutGap}
											onChange={(event) => handleLayoutGap(event)}
											onBlur={(event) => handleLayoutGapBlur(event)}
											onKeyPress={(event) => {
												if (event.key === 'Enter') {
													if (document.activeElement) {
														document.activeElement.blur();
													}
													handleLayoutGapBlur(event);
													event.preventDefault();
												}
											}}
											label={
												<FormattedMessage
													id="ShapeSettings.LayoutGap"
													defaultMessage="Layout Gap"
												/>
											}
										/>
								  ]
								: null,
							item.current.getParent() instanceof JSG.LayoutNode ? (
								<TextField
									key="layoutnewline"
									variant="outlined"
									fullWidth
									size="small"
									margin="normal"
									value={layoutNewLine}
									onChange={(event) => handleLayoutNewLine(event)}
									onBlur={(event) => handleLayoutNewLineBlur(event)}
									onKeyPress={(event) => {
										if (event.key === 'Enter') {
											if (document.activeElement) {
												document.activeElement.blur();
											}
											handleLayoutNewLineBlur(event);
											event.preventDefault();
										}
									}}
									label={
										<FormattedMessage
											id="ShapeSettings.LayoutNewLine"
											defaultMessage="Wrap behind Cell Treshold"
										/>
									}
								/>
							) : null
						]}
					</div>
				);
			case 'layoutnode':
				return (
					<div>
						<TextField
							key="layoutmode"
							variant="outlined"
							size="small"
							fullWidth
							margin="normal"
							select
							value={getLayoutMode('layoutmode')}
							onChange={(event) =>
								handleLayoutMode(event, 'layoutmode', new JSG.StringExpression(event.target.value))
							}
							label={<FormattedMessage id="ShapeSettings.LayoutMode" defaultMessage="Layout Mode" />}
						>
							<MenuItem value="left">
								<FormattedMessage id="ShapeSettings.LayoutLeft" defaultMessage="Left" />
							</MenuItem>
							<MenuItem value="center">
								<FormattedMessage id="ShapeSettings.LayoutCenter" defaultMessage="Center" />
							</MenuItem>
							<MenuItem value="resize">
								<FormattedMessage id="ShapeSettings.LayoutResize" defaultMessage="Resize" />
							</MenuItem>
						</TextField>
						{getLayoutMode('layoutmode') === 'resize' ? [
						<TextField
							key="layoutminwidth"
							variant="outlined"
							fullWidth
							size="small"
							margin="normal"
							value={getLayoutMode('minwidth')}
							onChange={(event) =>
								handleLayoutMode(event, 'minwidth', new JSG.NumberExpression(event.target.value))
							}
							label={
								<FormattedMessage id="ShapeSettings.MinimumWidth" defaultMessage="Minimum Width" />
							}
						/>,
						<TextField
							key="layoutmaxwidth"
							variant="outlined"
							fullWidth
							size="small"
							margin="normal"
							value={getLayoutMode('maxwidth')}
							onChange={(event) =>
								handleLayoutMode(event, 'maxwidth', new JSG.NumberExpression(event.target.value))
							}
							label={
								<FormattedMessage id="ShapeSettings.MaximumWidth" defaultMessage="Maximum Width" />
							}
						/>] : null}
					</div>
				);
			case 'processsheet':
				return <div>{[getAttributeHandler('ShapeSettings.SourceRange', 'range', 'range')]}</div>;
			default:
				return null;
		}
	};

	const onShapeSelection = () => {
		const viewer = graphManager.getGraphViewer();
		const selection = viewer.getSelection();
		const shtView = graphManager.getActiveSheetView();
		const valid = shtView && selection.length === 1;
		const itm = valid ? selection[0].getModel() : undefined;

		setItem(itm);
		setView(valid ? selection[0].getView() : undefined);
		setSheetView(valid ? shtView : undefined);

		let sheet = itm;
		while (sheet && !(sheet instanceof JSG.StreamSheet)) {
			sheet = sheet.getParent();
		}
		setShapeSheet(valid ? sheet : undefined);

		if (itm instanceof JSG.LayoutCell) {
			setLayoutGap(itm.getLayoutCellAttributes().getGap().getValue());
			setLayoutMargin(itm.getLayoutCellAttributes().getMargin().getValue());
			setLayoutNewLine(itm.getLayoutCellAttributes().getNewLine().getValue());
		}

		if (!itm) {
			setTabSelected(undefined);
		} else {
			const title = getComponentTitle();
			if (tabSelected.current === undefined) {
				setTabSelected(title ? 'component' : 'general');
			} else if (!title && tabSelected.current === 'component') {
				setTabSelected('general');
			}
		}
	};

	const isPanelVisible = (panel) => {
		if (item.current instanceof JSG.LayoutNode || item.current instanceof JSG.LayoutCell) {
			return panel === 'panelFormat';
		}

		return true;
	};

	useEffect(() => {
		onShapeSelection();
	}, [props.open]);

	const getHelpContext = () => {
		if (item.current) {
			switch (item.current.getItemType()) {
			case 'layoutnode':
				return 'guides/dashboard/#dashboard-settings';
			case 'layoutcell':
				return 'guides/dashboard/#matrix-cell';
			case 'streamchart':
				return 'guides/charts/chartsettings';
			default:
				break;
			}
		}

		return 'guides/shapes/shapesettings';
	}

	const onCommand = () => {
		// rerender after toolbar change
		setRandom(Math.random);
	};

	useJSGNotification(JSG.COMMAND_EXECUTED_NOTIFICATION, onCommand);
	useJSGNotification(JSG.SelectionProvider.SELECTION_CHANGED_NOTIFICATION, onShapeSelection);

	const line = item.current ? item.current.getShape().getType() === JSG.LineShape.TYPE : false;
	const component = getComponentTitle();

	return (
		<SettingsPaper
			open={props.open}
			close={() => props.close()}
			helpContext={getHelpContext()}
			title={getSettingsTitle()}
		>
			{item.current && tabSelected.current && (tabSelected.current === 'general' || component) ? (
				<Tabs
					sx={{ minHeight: '30px', marginBottom: '14px' }}
					value={tabSelected.current}
					onChange={(event, value) => setTabSelected(value)}
				>
					{component ? (
						<Tab value="component" sx={{ padding: '0px', minHeight: '24px' }} label={component} />
					) : null}
					<Tab
						value="general"
						sx={{ padding: '0px', minHeight: '24px' }}
						label={<FormattedMessage id="ShapeSettings.General" defaultMessage="General" />}
					/>
				</Tabs>
			) : null}
			{item.current && sheetView.current && tabSelected.current === 'general'
				? [
						isPanelVisible('panelGeometrie') ? (
							<StreamSheetAccordion
								id="panelGeometrie"
								key="panelGeometrie"
								expanded={expanded === 'panelGeometrie'}
								onChange={() => handleChange('panelGeometrie')}
								title={<FormattedMessage id="ShapeSettings.Geometrie" defaultMessage="Geometrie" />}
							>
								{expanded === 'panelGeometrie' ? (
									<FormGroup>
										{item.current.getParent().getItemType() === 'layoutcell'
											? getAttributeHandler(
													'ShapeSettings.Alignment',
													getItemPath(JSG.ItemAttributes.HALIGN),
													'enum',
													0,
													[
														{ value: '0', label: 'ShapeSettings.WidthFromParent' },
														{ value: '1', label: 'ShapeSettings.Left' },
														{ value: '2', label: 'ShapeSettings.AlignCenter' },
														{ value: '3', label: 'ShapeSettings.Right' },
														{ value: '4', label: 'ShapeSettings.None' }
													]
											  )
											: null}
										{item.current.getParent().getItemType() === 'cellsnode'
											? getAttributeHandler(
													'ShapeSettings.CellBased',
													getItemPath(JSG.ItemAttributes.CELLBASED),
													'bool',
													0
											  )
											: null}
										{getPropertyHandler(
											line ? 'ShapeSettings.StartX' : 'ShapeSettings.HorizontalPosition',
											handleX,
											getX(),
											validateCoordinate
										)}
										{getPropertyHandler(
											line ? 'ShapeSettings.StartY' : 'ShapeSettings.VerticalPosition',
											handleY,
											getY(),
											validateCoordinate
										)}
										{getPropertyHandler(
											line ? 'ShapeSettings.EndX' : 'ShapeSettings.Width',
											handleWidth,
											getWidth(),
											validateCoordinate
										)}
										{getPropertyHandler(
											line ? 'ShapeSettings.EndY' : 'ShapeSettings.Height',
											handleHeight,
											getHeight(),
											validateCoordinate
										)}
										{line
											? null
											: getPropertyHandler(
													'ShapeSettings.Rotation',
													handleRotation,
													item.current.getAngle(),
													undefined,
													2
											  )}
										{line ? null : (
											<TextField
												variant="outlined"
												size="small"
												fullWidth
												margin="normal"
												select
												value={getRotationCenter()}
												onChange={(event) => handleRotationCenter(event)}
												label={
													<FormattedMessage
														id="ShapeSettings.RotationCenter"
														defaultMessage="Rotation Center"
													/>
												}
												id="shaperotation"
											>
												<MenuItem value="0">
													<FormattedMessage
														id="ShapeSettings.LeftTop"
														defaultMessage="Left Top"
													/>
												</MenuItem>
												<MenuItem value="1">
													<FormattedMessage
														id="ShapeSettings.CenterTop"
														defaultMessage="Left Top"
													/>
												</MenuItem>
												<MenuItem value="2">
													<FormattedMessage
														id="ShapeSettings.RightTop"
														defaultMessage="Left Top"
													/>
												</MenuItem>
												<MenuItem value="3">
													<FormattedMessage
														id="ShapeSettings.LeftMiddle"
														defaultMessage="Left Top"
													/>
												</MenuItem>
												<MenuItem value="4">
													<FormattedMessage
														id="ShapeSettings.Center"
														defaultMessage="Left Top"
													/>
												</MenuItem>
												<MenuItem value="5">
													<FormattedMessage
														id="ShapeSettings.RightMiddle"
														defaultMessage="Left Top"
													/>
												</MenuItem>
												<MenuItem value="6">
													<FormattedMessage
														id="ShapeSettings.LeftBottom"
														defaultMessage="Left Top"
													/>
												</MenuItem>
												<MenuItem value="7">
													<FormattedMessage
														id="ShapeSettings.CenterBottom"
														defaultMessage="Left Top"
													/>
												</MenuItem>
												<MenuItem value="8">
													<FormattedMessage
														id="ShapeSettings.RightBottom"
														defaultMessage="Left Top"
													/>
												</MenuItem>
											</TextField>
										)}
										{item.current.getShape() instanceof JSG.PolygonShape &&
										!(item.current.getShape() instanceof JSG.EllipseShape)
											? getPropertyHandler(
													'ShapeSettings.PointRange',
													handlePointRange,
													item.current.getShape().getSource()
											  )
											: null}
										{item.current.getShape() instanceof JSG.PolygonShape &&
										!(item.current.getShape() instanceof JSG.EllipseShape)
											? getAttributeHandler(
													'ShapeSettings.Closed',
													getItemPath(JSG.ItemAttributes.CLOSED),
													'bool',
													0
											  )
											: null}
									</FormGroup>
								) : null}
							</StreamSheetAccordion>
						) : null,
						isPanelVisible('panelFormat') ? (
							<StreamSheetAccordion
								id="panelFormat"
								key="panelFormat"
								expanded={expanded === 'panelFormat'}
								onChange={() => handleChange('panelFormat')}
								title={<FormattedMessage id="ShapeSettings.Format" defaultMessage="Format" />}
							>
								{expanded === 'panelFormat' ? (
									<FormGroup>
										<Typography
											variant="subtitle1"
											style={{ marginTop: '5px', marginBottom: '5px' }}
										>
											<FormattedMessage id="ShapeSettings.Line" defaultMessage="Line" />
										</Typography>
										{getAttributeHandler(
											'ShapeSettings.LineColor',
											getFormatPath(JSG.FormatAttributes.LINECOLOR),
											'color',
											0,
											'color'
										)}
										{getAttributeHandler(
											'ShapeSettings.LineWidth',
											getFormatPath(JSG.FormatAttributes.LINEWIDTH),
											'number',
											0
										)}
										{getAttributeHandler(
											'ShapeSettings.LineStyle',
											getFormatPath(JSG.FormatAttributes.LINESTYLE),
											'enum',
											0,
											[
												{ value: '0', label: 'ShapeSettings.NoLS' },
												{ value: '1', label: 'ShapeSettings.SolidLS' },
												{ value: '2', label: 'ShapeSettings.Dot' },
												{ value: '3', label: 'ShapeSettings.Dash' },
												{ value: '4', label: 'ShapeSettings.DashDot' },
												{ value: '5', label: 'ShapeSettings.DashDotDot' },
												{ value: '6', label: 'ShapeSettings.ShortDash' },
												{ value: '7', label: 'ShapeSettings.LongDash' }
											]
										)}
										{item.current.getItemAttributes().getClosed().getValue() === false
											? getAttributeHandler(
													'ShapeSettings.StartArrow',
													getFormatPath(JSG.FormatAttributes.LINEARROWSTART),
													'enum',
													0,
													[
														{ value: '0', label: 'ShapeSettings.NoArrow' },
														{ value: '1', label: 'ShapeSettings.ArrowFilled' },
														{ value: '2', label: 'ShapeSettings.ArrowFilledSmall' },
														{ value: '7', label: 'ShapeSettings.ArrowDoubleFilled' },
														{ value: '14', label: 'ShapeSettings.ArrowReverse' },
														{ value: '22', label: 'ShapeSettings.ArrowCircle' },
														{ value: '24', label: 'ShapeSettings.ArrowDiamond' },
														{ value: '29', label: 'ShapeSettings.ArrowDoubleLine' },
														{ value: '30', label: 'ShapeSettings.ArrowSquare' }
													]
											  )
											: null}
										{item.current.getItemAttributes().getClosed().getValue() === false
											? getAttributeHandler(
													'ShapeSettings.EndArrow',
													getFormatPath(JSG.FormatAttributes.LINEARROWEND),
													'enum',
													0,
													[
														{ value: '0', label: 'ShapeSettings.NoArrow' },
														{ value: '1', label: 'ShapeSettings.ArrowFilled' },
														{ value: '2', label: 'ShapeSettings.ArrowFilledSmall' },
														{ value: '7', label: 'ShapeSettings.ArrowDoubleFilled' },
														{ value: '14', label: 'ShapeSettings.ArrowReverse' },
														{ value: '22', label: 'ShapeSettings.ArrowCircle' },
														{ value: '24', label: 'ShapeSettings.ArrowDiamond' },
														{ value: '29', label: 'ShapeSettings.ArrowDoubleLine' },
														{ value: '30', label: 'ShapeSettings.ArrowSquare' }
													]
											  )
											: null}
										{getAttributeHandler(
											'ShapeSettings.LineCorner',
											getFormatPath(JSG.FormatAttributes.LINECORNER),
											'number',
											0
										)}
										<Typography
											variant="subtitle1"
											style={{ marginTop: '8px', marginBottom: '5px' }}
										>
											<FormattedMessage id="ShapeSettings.Fill" defaultMessage="Fill" />
										</Typography>
										{getAttributeHandler(
											'ShapeSettings.FillStyle',
											getFormatPath(JSG.FormatAttributes.FILLSTYLE),
											'enum',
											0,
											[
												{ value: '0', label: 'ShapeSettings.None' },
												{ value: '1', label: 'ShapeSettings.Solid' },
												{ value: '2', label: 'ShapeSettings.Gradient' },
												{ value: '3', label: 'ShapeSettings.Pattern' }
											]
										)}
										{item.current.getFormat().getFillStyle().getValue()
											? getAttributeHandler(
													'ShapeSettings.FillColor',
													getFormatPath(JSG.FormatAttributes.FILLCOLOR),
													'color',
													0,
													'color'
											  )
											: null}
										{item.current.getFormat().getFillStyle().getValue() === 2
											? [
													getAttributeHandler(
														'ShapeSettings.FillColorBack',
														getFormatPath(JSG.FormatAttributes.GRADIENTCOLOR),
														'color',
														0,
														'color'
													),
													getAttributeHandler(
														'ShapeSettings.GradientStyle',
														getFormatPath(JSG.FormatAttributes.GRADIENTTYPE),
														'enum',
														0,
														[
															{ value: '0', label: 'ShapeSettings.Linear' },
															{ value: '1', label: 'ShapeSettings.Radial' }
														]
													)
											  ]
											: null}
										{item.current.getFormat().getFillStyle().getValue() === 2 &&
										item.current.getFormat().getGradientType().getValue() === 0
											? getAttributeHandler(
													'ShapeSettings.GradientAngle',
													getFormatPath(JSG.FormatAttributes.GRADIENTANGLE),
													'number',
													0
											  )
											: null}
										{item.current.getFormat().getFillStyle().getValue() === 2 &&
										item.current.getFormat().getGradientType().getValue() === 1
											? [
													getAttributeHandler(
														'ShapeSettings.XOFFSET',
														getFormatPath(JSG.FormatAttributes.GRADIENTOFFSET_X),
														'number',
														0
													),
													getAttributeHandler(
														'ShapeSettings.YOFFSET',
														getFormatPath(JSG.FormatAttributes.GRADIENTOFFSET_Y),
														'number',
														0
													)
											  ]
											: null}
										{item.current.getFormat().getFillStyle().getValue() === 3
											? getAttributeHandler(
													'ShapeSettings.Pattern',
													getFormatPath(JSG.FormatAttributes.PATTERN),
													'string',
													0
											  )
											: null}
									</FormGroup>
								) : null}
							</StreamSheetAccordion>
						) : null,
						isPanelVisible('panelText') ? (
							<StreamSheetAccordion
								id="panelText"
								key="panelText"
								expanded={expanded === 'panelText'}
								onChange={() => handleChange('panelText')}
								title={<FormattedMessage id="ShapeSettings.TextFormat" defaultMessage="Text Format" />}
							>
								{expanded === 'panelText' ? (
									<FormGroup>
										{getAttributeHandler(
											'ShapeSettings.FontName',
											getTextFormatPath(JSG.TextFormatAttributes.FONTNAME),
											'enum',
											0,
											[
												{ value: 'Arial' },
												{ value: 'Courier New' },
												{ value: 'Georgia' },
												{ value: 'Lucida' },
												{ value: 'Lucida Console' },
												{ value: 'Palatino' },
												{ value: 'Roboto' },
												{ value: 'Tahoma' },
												{ value: 'Trebuchet MS' },
												{ value: 'Verdana' }
											]
										)}
										{getAttributeHandler(
											'ShapeSettings.FontSize',
											getTextFormatPath(JSG.TextFormatAttributes.FONTSIZE),
											'enum',
											0,
											[
												{ value: '7' },
												{ value: '8' },
												{ value: '9' },
												{ value: '10' },
												{ value: '11' },
												{ value: '12' },
												{ value: '14' },
												{ value: '18' },
												{ value: '24' },
												{ value: '36' }
											]
										)}
										{getAttributeHandler(
											'ShapeSettings.FontColor',
											getTextFormatPath(JSG.TextFormatAttributes.FONTCOLOR),
											'color',
											0,
											'color'
										)}
										{getAttributeHandler(
											'ShapeSettings.FontStyle',
											getTextFormatPath(JSG.TextFormatAttributes.FONTSTYLE),
											'enum',
											0,
											[
												{ value: '0', label: 'ShapeSettings.Normal' },
												{ value: '1', label: 'ShapeSettings.Bold' },
												{ value: '2', label: 'ShapeSettings.Italic' },
												{ value: '3', label: 'ShapeSettings.BoldItalic' }
											]
										)}
										{getAttributeHandler(
											'ShapeSettings.Alignment',
											getTextFormatPath(JSG.TextFormatAttributes.HORIZONTALALIGN),
											'enum',
											0,
											[
												{ value: '0', label: 'ShapeSettings.Left' },
												{ value: '1', label: 'ShapeSettings.AlignCenter' },
												{ value: '2', label: 'ShapeSettings.Right' }
											]
										)}
										{getAttributeHandler(
											'ShapeSettings.HorizontalPosition',
											getTextFormatPath(JSG.TextFormatAttributes.HORIZONTALPOSITION),
											'enum',
											0,
											[
												{ value: '0', label: 'ShapeSettings.Custom' },
												{ value: '1', label: 'ShapeSettings.LeftOutside' },
												{ value: '2', label: 'ShapeSettings.Left' },
												{ value: '3', label: 'ShapeSettings.Center' },
												{ value: '4', label: 'ShapeSettings.Right' },
												{ value: '5', label: 'ShapeSettings.RightOutSide' }
											]
										)}
										{getAttributeHandler(
											'ShapeSettings.VerticalPosition',
											getTextFormatPath(JSG.TextFormatAttributes.VERTICALPOSITION),
											'enum',
											0,
											[
												{ value: '0', label: 'ShapeSettings.Custom' },
												{ value: '1', label: 'ShapeSettings.Above' },
												{ value: '2', label: 'ShapeSettings.Top' },
												{ value: '3', label: 'ShapeSettings.Center' },
												{ value: '4', label: 'ShapeSettings.Bottom' },
												{ value: '5', label: 'ShapeSettings.Below' }
											]
										)}
										<Grid container spacing={2}>
											<Grid item xs={6}>
												{getAttributeHandler(
													'ShapeSettings.LeftMargin',
													getItemPath(JSG.ItemAttributes.MARGINLEFT),
													'number',
													0
												)}
											</Grid>
											<Grid item xs={6}>
												{getAttributeHandler(
													'ShapeSettings.RightMargin',
													getItemPath(JSG.ItemAttributes.MARGINRIGHT),
													'number',
													0
												)}
											</Grid>
										</Grid>
										<Grid container spacing={2}>
											<Grid item xs={6}>
												{getAttributeHandler(
													'ShapeSettings.TopMargin',
													getItemPath(JSG.ItemAttributes.MARGINTOP),
													'number',
													0
												)}
											</Grid>
											<Grid item xs={6}>
												{getAttributeHandler(
													'ShapeSettings.BottomMargin',
													getItemPath(JSG.ItemAttributes.MARGINBOTTOM),
													'number',
													0
												)}
											</Grid>
										</Grid>
									</FormGroup>
								) : null}
							</StreamSheetAccordion>
						) : null,
						isPanelVisible('panelName') ? (
							<StreamSheetAccordion
								id="panelName"
								key="panelName"
								expanded={expanded === 'panelName'}
								onChange={() => handleChange('panelName')}
								title={<FormattedMessage id="ShapeSettings.Name" defaultMessage="Name and Label" />}
							>
								{expanded === 'panelName' ? (
									<FormGroup>
										{getPropertyHandler(
											'ShapeSettings.Name',
											handleName,
											item.current.getName(),
											validateName
										)}
										{getAttributeHandler(
											'ShapeSettings.Label',
											getItemPath(JSG.ItemAttributes.LABEL),
											'string',
											0
										)}
									</FormGroup>
								) : null}
							</StreamSheetAccordion>
						) : null,
						isPanelVisible('panelAttributes') ? (
							<StreamSheetAccordion
								id="panelAttributes"
								key="panelAttributes"
								expanded={expanded === 'panelAttributes'}
								onChange={() => handleChange('panelAttributes')}
								title={<FormattedMessage id="ShapeSettings.Attributes" defaultMessage="Attributes" />}
							>
								{expanded === 'panelAttributes' ? (
									<FormGroup>
										{getAttributeHandler(
											'ShapeSettings.Container',
											getItemPath(JSG.ItemAttributes.CONTAINER),
											'bool',
											0
										)}
										{getAttributeHandler(
											'ShapeSettings.Clip',
											getItemPath(JSG.ItemAttributes.CLIPCHILDREN),
											'bool',
											0
										)}
										{getAttributeHandler(
											'ShapeSettings.Visible',
											getItemPath(JSG.ItemAttributes.VISIBLE),
											'bool',
											0
										)}
										{getAttributeHandler(
											'ShapeSettings.Sizeable',
											getItemPath(JSG.ItemAttributes.SIZEABLE),
											'bool',
											0
										)}
										{getAttributeHandler(
											'ShapeSettings.Selection',
											getItemPath(JSG.ItemAttributes.SELECTIONMODE),
											'enum',
											0,
											[
												{ value: '0', label: 'ShapeSettings.NotSelectable' },
												{ value: '1', label: 'ShapeSettings.OnBorder' },
												{ value: '2', label: 'ShapeSettings.OnArea' },
												{ value: '4', label: 'ShapeSettings.Default' }
											]
										)}
										{getAttributeHandler(
											'ShapeSettings.Shapes',
											getItemPath(JSG.ItemAttributes.SHAPES),
											'range',
											0
										)}
										{/*{getAttributeHandler("ShapeSettings.Deletable", getItemPath(JSG.ItemAttributes.DELETEABLE), 0)}*/}
										{/*{getAttributeHandler("ShapeSettings.Moveable", getItemPath(JSG.ItemAttributes.MOVEABLE), 0,  [*/}
										{/*	{ value: '0', label: 'ShapeSettings.NotMoveable'},*/}
										{/*	{ value: '1', label: 'ShapeSettings.OnlyVertical'},*/}
										{/*	{ value: '2', label: 'ShapeSettings.OnlyHorizontal'},*/}
										{/*	{ value: '3', label: 'ShapeSettings.MoveFree'},*/}
										{/*])}*/}
									</FormGroup>
								) : null}
							</StreamSheetAccordion>
						) : null,
						isPanelVisible('panelEvents') ? (
							<StreamSheetAccordion
								id="panelEvents"
								key="panelEvents"
								expanded={expanded === 'panelEvents'}
								onChange={() => handleChange('panelEvents')}
								title={<FormattedMessage id="ShapeSettings.Events" defaultMessage="Events" />}
							>
								{expanded === 'panelEvents' ? (
									<FormGroup>
										{getEventAttributeHandler(
											'ShapeSettings.OnClick',
											getEventPath(JSG.EventAttributes.ONCLICK)
										)}
										{getEventAttributeHandler(
											'ShapeSettings.OnDoubleClick',
											getEventPath(JSG.EventAttributes.ONDOUBLECLICK)
										)}
										{getEventAttributeHandler(
											'ShapeSettings.OnMouseDown',
											getEventPath(JSG.EventAttributes.ONMOUSEDOWN)
										)}
										{getEventAttributeHandler(
											'ShapeSettings.OnMouseUp',
											getEventPath(JSG.EventAttributes.ONMOUSEUP),
											0
										)}
										{item.current instanceof JSG.SheetKnobNode ||
										item.current instanceof JSG.SheetSliderNode
											? [
													getEventAttributeHandler(
														'ShapeSettings.OnValueChange',
														getEventPath(JSG.EventAttributes.ONVALUECHANGE)
													)
											  ]
											: null}
									</FormGroup>
								) : null}
							</StreamSheetAccordion>
						) : null
				  ]
				: null}
			{item.current && sheetView.current && tabSelected.current === 'component' ? getComponent(component) : null}
			{!item.current || !sheetView.current ? [
				<Typography
					sx={{mt: '15px'}}
					color="text.primary"
				>
					<FormattedMessage id="ShapeSettings.NoShape" defaultMessage="No Shape Selected" />
				</Typography>,
				<Button
					sx={{mt: '20px'}}
					variant="outlined"
					onClick={() => {props.showDialog('ShapeListSettings')}}
				>
					<FormattedMessage id="ShapeSettings.ViewShapeList" defaultMessage="View Shape List" />
				</Button>
			] : null}
		</SettingsPaper>
	);
}
